import { useMutation, useQueryClient } from "@tanstack/react-query";
// Type-only, and from the DECLARING module rather than the package barrel.
// A value import of the barrel from inside the barrel's own graph is a cycle:
// `index.ts` reaches this hook, this hook re-enters `index.ts`, and whichever
// binding the barrel had not declared yet is in TDZ. TypeScript's re-export
// getters tolerate that; swc's `const` bindings do not, and it surfaced as
// "ReferenceError: Cannot access '_ActionButtonGroup' before initialization"
// taking out 7 hub suites. `import type` is erased, so there is no cycle at all.
import type { CalendarEvent } from "../types/calendar.js";

interface DeleteEventRequest {
  eventId: string;
  source: string;
}

interface DeleteEventResponse {
  success: boolean;
  message: string;
}

const deleteGoogleEvent = async (eventId: string): Promise<DeleteEventResponse> => {
  const response = await fetch('/api/unifiedtime/google/create', {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ eventId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to delete Google Calendar event');
  }

  return response.json();
};

const deleteMicrosoftEvent = async (eventId: string): Promise<DeleteEventResponse> => {
  const response = await fetch('/api/unifiedtime/getCalendar/msft', {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ eventId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to delete Microsoft Calendar event');
  }

  return response.json();
};

export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, source }: DeleteEventRequest): Promise<DeleteEventResponse> => {
      const sourceLower = source.toLowerCase();
      
      if (sourceLower.includes('google') || sourceLower.includes('gmail')) {
        return deleteGoogleEvent(eventId);
      } else if (sourceLower.includes('microsoft') || sourceLower.includes('outlook')) {
        return deleteMicrosoftEvent(eventId);
      } else {
        throw new Error(`Unknown event source: ${source}`);
      }
    },
    retry: 3, // Retry up to 3 times on failure
    onSuccess: () => {
      // Invalidate and refetch calendar data
      queryClient.invalidateQueries({ queryKey: ["combined-calendar"] });
      queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
      queryClient.invalidateQueries({ queryKey: ["microsoft-calendar"] });
    },
  });
}
